import React, { useEffect, useRef, useState } from 'react';
import { Input } from '../../ui';
import { Upload, FileText, Trash2 } from 'lucide-react';

interface AiResponseConfigProps {
  config: any;
  onChange: (key: string, value: any) => void;
  onBatchChange?: (changes: Record<string, any>) => void;
}

const KNOWN_MODELS = new Set<string>([
  'gpt-6-luna',
  'gpt-6-sol',
  'gpt-6-astra',
  'gpt-5.6-luna',
  'gpt-5.6-terra',
  'gpt-5.6-sol',
  'gpt-5-nano',
  'gpt-5-mini',
  'gpt-5',
  'gpt-5.1',
  'gpt-5.2',
  'gpt-5-pro',
  'gpt-5.2-pro',
  'gpt-4.1-nano',
  'gpt-4.1-mini',
  'gpt-4.1',
  'gpt-4o-mini',
  'gpt-4o',
  'o4-mini',
  'o3-mini',
  'o3',
  'o3-pro',
  'gpt-4-turbo',
  'gpt-4',
  'gpt-3.5-turbo',
  'gemini-3.8-flash',
  'gemini-3.5-flash-lite',
  'gemini-3.1-flash-lite',
  'gemini-3.7-flash',
  'gemini-3.6-flash',
  'gemini-3.5-flash',
  'gemini-2.5-pro',
  'gemini-2.5-flash',
  'gemini-2.5-flash-lite',
  'gemini-2.0-flash',
  'gemini-2.0-flash-lite',
  'gemini-1.5-pro',
  'gemini-1.5-flash',
  'gemini-1.5-flash-8b',
  'claude-haiku-4-5',
  'claude-sonnet-5',
  'claude-opus-5-5',
  'claude-fable-5-1',
  'claude-opus-5',
  'claude-opus-4-8',
  'claude-opus-4-7',
  'claude-fable-5',
  'claude-sonnet-4-6',
  'claude-opus-4-6',
  'claude-sonnet-4-5',
]);

export const AiResponseConfig: React.FC<AiResponseConfigProps> = ({ config, onChange, onBatchChange }) => {
  const provider = config.provider || 'openai';
  const [customModel, setCustomModel] = useState(false);
  const isCustomModel = customModel || (!!config.model && !KNOWN_MODELS.has(config.model));

  // Temperatura só é aplicada por OpenAI (modelos não-raciocínio) e Gemini.
  // Claude e os modelos de raciocínio OpenAI (GPT-5* e série o) ignoram/rejeitam um valor custom —
  // o backend descarta a temperatura nesses casos, então escondemos o controle para não confundir.
  const modelStr = String(config.model || '').toLowerCase();
  const isOpenAiReasoning = provider === 'openai' && (/^gpt-[56]/.test(modelStr) || /^o[1-9]/.test(modelStr));
  const temperatureApplies = !(provider === 'claude' || isOpenAiReasoning);
  const [temperatureInput, setTemperatureInput] = useState<string>(
    config.temperature === undefined || config.temperature === null || config.temperature === ''
      ? '0.7'
      : String(config.temperature),
  );
  const [maxTokensInput, setMaxTokensInput] = useState<string>(
    config.maxTokens === undefined || config.maxTokens === null || config.maxTokens === ''
      ? '500'
      : String(config.maxTokens),
  );

  useEffect(() => {
    setTemperatureInput(
      config.temperature === undefined || config.temperature === null || config.temperature === ''
        ? '0.7'
        : String(config.temperature),
    );
  }, [config.temperature]);

  useEffect(() => {
    setMaxTokensInput(
      config.maxTokens === undefined || config.maxTokens === null || config.maxTokens === ''
        ? '500'
        : String(config.maxTokens),
    );
  }, [config.maxTokens]);

  const handleProviderChange = (newProvider: string) => {
    // Set sensible default model when switching providers
    const defaultModel =
      newProvider === 'gemini' ? 'gemini-2.0-flash'
      : newProvider === 'claude' ? 'claude-haiku-4-5'
      : 'gpt-6-luna';
    if (onBatchChange) {
      onBatchChange({ provider: newProvider, model: defaultModel });
    } else {
      onChange('provider', newProvider);
      onChange('model', defaultModel);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 leading-relaxed">
        <strong>Requisito obrigatório:</strong> este nó só funciona com conexão de IA ativa no aplicativo.
        Configure a chave em <strong>Configurações → Integrações</strong> (OpenAI, Gemini ou Claude).
        <br /><br />
        <strong>Importante:</strong> este nó não envia mensagem direto para o cliente. Ele só salva a resposta em variável.
        Use um nó <strong>Enviar Mensagem</strong> depois para responder ao cliente.
      </div>

      {provider === 'openai' ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-700 leading-relaxed">
          <strong>💡 Como funciona:</strong> Este nó envia a mensagem do cliente para a <strong>OpenAI</strong>, que gera uma resposta automática usando modelos como GPT-4.1 e GPT-5.
          <br /><br />
          <strong>🧠 Memória da conversa:</strong> A OpenAI <u>mantém o contexto automaticamente</u> via <em>previous_response_id</em>. Toda a conversa é lembrada enquanto o atendimento estiver ativo.
          <br /><br />
          <strong>📝 Prompt do Sistema:</strong> É a "personalidade" da IA. Aqui você define como ela deve se comportar — ex: "Seja educado, responda só sobre nossos produtos, nunca invente informações."
          <br /><br />
          <strong>🔑 Chave de API:</strong> Obtenha sua chave em <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline font-semibold">platform.openai.com</a> e configure em <u>Configurações → Integrações</u>.
        </div>
      ) : provider === 'gemini' ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700 leading-relaxed">
          <strong>💡 Como funciona:</strong> Este nó envia a mensagem do cliente para o <strong>Google Gemini</strong>, que gera uma resposta automática usando modelos como Gemini 2.0 Flash e 2.5 Pro.
          <br /><br />
          <strong>🧠 Memória da conversa:</strong> O Gemini mantém o <u>histórico completo da conversa</u> armazenado internamente. O contexto é preservado automaticamente enquanto o atendimento estiver ativo.
          <br /><br />
          <strong>📝 Prompt do Sistema:</strong> É a "personalidade" da IA. Aqui você define como ela deve se comportar — ex: "Seja educado, responda só sobre nossos produtos, nunca invente informações."
          <br /><br />
          <strong>🔑 Chave de API:</strong> Obtenha sua chave gratuitamente em <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer" className="underline font-semibold">Google AI Studio</a> e configure em <u>Configurações → Integrações</u>.
        </div>
      ) : (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-xs text-orange-700 leading-relaxed">
          <strong>💡 Como funciona:</strong> Este nó envia a mensagem do cliente para o <strong>Claude (Anthropic)</strong>, que gera uma resposta automática usando modelos como Claude Haiku 4.5, Sonnet 5 e Opus 4.8.
          <br /><br />
          <strong>🧠 Memória da conversa:</strong> O histórico completo da conversa é enviado ao Claude a cada mensagem, preservando o contexto durante todo o atendimento.
          <br /><br />
          <strong>📝 Prompt do Sistema:</strong> É a "personalidade" da IA. Aqui você define como ela deve se comportar — ex: "Seja educado, responda só sobre nossos produtos, nunca invente informações."
          <br /><br />
          <strong>🔑 Chave de API:</strong> Obtenha sua chave em <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="underline font-semibold">console.anthropic.com</a> e configure em <u>Configurações → Integrações</u>.
        </div>
      )}

      {/* Provider selector */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Provedor de IA
        </label>
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => handleProviderChange('openai')}
            className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
              provider === 'openai'
                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
            }`}
          >
            <img src="/providers/openai.svg" alt="OpenAI" className="h-5 w-auto" />
          </button>
          <button
            type="button"
            onClick={() => handleProviderChange('gemini')}
            className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
              provider === 'gemini'
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
            }`}
          >
            <img src="/providers/gemini.svg" alt="Google Gemini" className="h-5 w-auto" />
          </button>
          <button
            type="button"
            onClick={() => handleProviderChange('claude')}
            className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
              provider === 'claude'
                ? 'border-orange-500 bg-orange-50 text-orange-700'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
            }`}
          >
            <img src="/providers/claude.svg" alt="Claude" className="h-5 w-auto" />
          </button>
        </div>
      </div>

      {/* Model selector */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Modelo
        </label>
        <select
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={isCustomModel ? '__custom__' : (config.model || (provider === 'gemini' ? 'gemini-2.0-flash' : provider === 'claude' ? 'claude-haiku-4-5' : 'gpt-6-luna'))}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '__custom__') {
              setCustomModel(true);
              return;
            }
            setCustomModel(false);
            onChange('model', value);
          }}
        >
          {provider === 'openai' ? (
            <>
              <optgroup label="⭐ GPT-6 (mais recentes)">
                <option value="gpt-6-luna">GPT-6 Luna — rápido e o mais barato ✓ recomendado</option>
                <option value="gpt-6-sol">GPT-6 Sol — equilíbrio de inteligência e custo</option>
                <option value="gpt-6-astra">GPT-6 Astra — máxima capacidade</option>
              </optgroup>
              <optgroup label="GPT-5.6">
                <option value="gpt-5.6-luna">GPT-5.6 Luna — econômico</option>
                <option value="gpt-5.6-terra">GPT-5.6 Terra — equilibra inteligência e custo</option>
                <option value="gpt-5.6-sol">GPT-5.6 Sol — trabalho profissional complexo</option>
              </optgroup>
              <optgroup label="GPT-5">
                <option value="gpt-5-nano">GPT-5 Nano — mais rápido e barato</option>
                <option value="gpt-5-mini">GPT-5 Mini — rápido e inteligente</option>
                <option value="gpt-5">GPT-5 — muito inteligente, raciocínio avançado</option>
                <option value="gpt-5.1">GPT-5.1 — raciocínio ainda mais avançado</option>
                <option value="gpt-5.2">GPT-5.2 — modelo mais avançado disponível</option>
              </optgroup>
              <optgroup label="GPT-5 Pro (Máxima qualidade)">
                <option value="gpt-5-pro">GPT-5 Pro — respostas mais precisas</option>
                <option value="gpt-5.2-pro">GPT-5.2 Pro — máxima qualidade</option>
              </optgroup>
              <optgroup label="GPT-4.1 (Custo-benefício)">
                <option value="gpt-4.1-nano">GPT-4.1 Nano — ultra rápido e barato</option>
                <option value="gpt-4.1-mini">GPT-4.1 Mini — equilibrado</option>
                <option value="gpt-4.1">GPT-4.1 — mais inteligente sem raciocínio</option>
              </optgroup>
              <optgroup label="GPT-4o (Geração anterior)">
                <option value="gpt-4o-mini">GPT-4o Mini — rápido e acessível</option>
                <option value="gpt-4o">GPT-4o — rápido e flexível</option>
              </optgroup>
              <optgroup label="o-series (Raciocínio profundo)">
                <option value="o4-mini">o4-mini — raciocínio rápido</option>
                <option value="o3-mini">o3-mini — raciocínio compacto</option>
                <option value="o3">o3 — raciocínio complexo</option>
                <option value="o3-pro">o3-pro — raciocínio máximo</option>
              </optgroup>
              <optgroup label="Modelos legados">
                <option value="gpt-4-turbo">GPT-4 Turbo</option>
                <option value="gpt-4">GPT-4</option>
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo — mais barato</option>
              </optgroup>
            </>
          ) : provider === 'gemini' ? (
            <>
              <optgroup label="⭐ Gemini 3 (mais recentes)">
                <option value="gemini-3.8-flash">Gemini 3.8 Flash — o mais inteligente da linha ✓ recomendado</option>
                <option value="gemini-3.5-flash-lite">Gemini 3.5 Flash Lite — o mais rápido e barato</option>
                <option value="gemini-3.1-flash-lite">Gemini 3.1 Flash Lite — ótimo custo-benefício</option>
                <option value="gemini-3.7-flash">Gemini 3.7 Flash — geração anterior</option>
                <option value="gemini-3.6-flash">Gemini 3.6 Flash — geração anterior</option>
                <option value="gemini-3.5-flash">Gemini 3.5 Flash — geração anterior</option>
              </optgroup>
              <optgroup label="Gemini 2.5">
                <option value="gemini-2.5-pro">Gemini 2.5 Pro — máxima qualidade, pensamento</option>
                <option value="gemini-2.5-flash">Gemini 2.5 Flash — rápido com pensamento</option>
                <option value="gemini-2.5-flash-lite">Gemini 2.5 Flash Lite — o mais barato da 2.5</option>
              </optgroup>
              <optgroup label="Gemini 2.0 (Custo-benefício)">
                <option value="gemini-2.0-flash">Gemini 2.0 Flash — equilibrado</option>
                <option value="gemini-2.0-flash-lite">Gemini 2.0 Flash Lite — mais barato e rápido</option>
              </optgroup>
              <optgroup label="Gemini 1.5 (Geração anterior)">
                <option value="gemini-1.5-pro">Gemini 1.5 Pro — contexto longo</option>
                <option value="gemini-1.5-flash">Gemini 1.5 Flash — uso geral</option>
                <option value="gemini-1.5-flash-8b">Gemini 1.5 Flash 8B — mais leve</option>
              </optgroup>
            </>
          ) : (
            <>
              <optgroup label="⭐ Recomendados (atendimento)">
                <option value="claude-haiku-4-5">Claude Haiku 4.5 — rápido e econômico ✓ recomendado</option>
                <option value="claude-sonnet-5">Claude Sonnet 5 — equilíbrio de velocidade e inteligência</option>
              </optgroup>
              <optgroup label="Máxima qualidade">
                <option value="claude-opus-5-5">Claude Opus 5.5 — indicado pela Anthropic p/ a maioria dos usos</option>
                <option value="claude-fable-5-1">Claude Fable 5.1 — raciocínio exigente (mais caro e lento)</option>
              </optgroup>
              <optgroup label="Geração anterior">
                <option value="claude-opus-5">Claude Opus 5</option>
                <option value="claude-opus-4-8">Claude Opus 4.8</option>
                <option value="claude-opus-4-7">Claude Opus 4.7</option>
                <option value="claude-fable-5">Claude Fable 5</option>
                <option value="claude-sonnet-4-6">Claude Sonnet 4.6</option>
                <option value="claude-opus-4-6">Claude Opus 4.6</option>
                <option value="claude-sonnet-4-5">Claude Sonnet 4.5</option>
              </optgroup>
            </>
          )}
          <option value="__custom__">✏️ Outro modelo — digitar o ID…</option>
        </select>
        {isCustomModel && (
          <input
            type="text"
            className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="ID exato do modelo (ex.: gpt-5.3-luna)"
            value={config.model || ''}
            onChange={(e) => onChange('model', e.target.value.trim())}
            autoFocus
          />
        )}
        <p className="mt-1 text-xs text-slate-500">
          {provider === 'openai'
            ? 'O GPT-6 Luna é hoje o mais barato da OpenAI e supera o GPT-4.1 Mini.'
            : 'O Gemini 2.0 Flash oferece o melhor custo-benefício para chatbots.'}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Prompt do Sistema
        </label>
        <textarea
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={4}
          placeholder="Você é um assistente de atendimento..."
          value={config.systemPrompt || ''}
          onChange={(e) => onChange('systemPrompt', e.target.value)}
        />
        <p className="mt-1 text-xs text-slate-500">
          Use {'{{variavel}}'} para inserir variáveis dinâmicas no prompt.
        </p>
      </div>

      {/* Knowledge Base */}
      <KnowledgeBaseSection config={config} onChange={onChange} />

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Variável da mensagem do usuário
        </label>
        <Input
          placeholder="Padrão: lastMessage"
          value={config.userMessageVariable || ''}
          onChange={(e) => onChange('userMessageVariable', e.target.value.replace(/\s/g, '_'))}
        />
        <p className="mt-1 text-xs text-slate-500">
          Deixe vazio para usar a última mensagem recebida.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Temperatura
          </label>
          {temperatureApplies ? (
            <>
              <input
                type="number"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                min={0}
                max={2}
                step={0.1}
                value={temperatureInput}
                onChange={(e) => {
                  setTemperatureInput(e.target.value);
                }}
                onBlur={() => {
                  const parsed = parseFloat(temperatureInput);
                  const normalized = Number.isFinite(parsed)
                    ? Math.min(2, Math.max(0, parsed))
                    : 0.7;
                  const rounded = Number(normalized.toFixed(2));
                  setTemperatureInput(String(rounded));
                  onChange('temperature', rounded);
                }}
              />
              <p className="mt-1 text-xs text-slate-500">0 = preciso, 2 = criativo</p>
            </>
          ) : (
            <>
              <div className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-400 select-none">
                Automática
              </div>
              <p className="mt-1 text-xs text-slate-500">
                {provider === 'claude'
                  ? 'Modelos Claude não aceitam ajuste de temperatura — ajuste o comportamento pelo prompt.'
                  : 'Modelos de raciocínio (GPT-5 / série o) usam temperatura fixa e ignoram este ajuste.'}
              </p>
            </>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Max Tokens
          </label>
          <input
            type="number"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            min={50}
            max={4096}
            step={50}
            value={maxTokensInput}
            onChange={(e) => {
              setMaxTokensInput(e.target.value);
            }}
            onBlur={() => {
              const parsed = parseInt(maxTokensInput, 10);
              const normalized = Number.isFinite(parsed)
                ? Math.min(4096, Math.max(50, parsed))
                : 500;
              setMaxTokensInput(String(normalized));
              onChange('maxTokens', normalized);
            }}
          />
          <p className="mt-1 text-xs text-slate-500">Limite de tokens na resposta</p>
        </div>
      </div>

      <Input
        label="Salvar resposta em (obrigatório)"
        placeholder="Ex: resposta_ia"
        value={config.responseVariable || ''}
        onChange={(e) => onChange('responseVariable', e.target.value.replace(/\s/g, '_'))}
      />
      <p className="mt-1 text-xs text-slate-500">
        Dica: no nó Enviar Mensagem, use a variável {`{{${config.responseVariable || 'ai_response'}}}`}.
      </p>
    </div>
  );
};

const MAX_FILE_SIZE = 100 * 1024; // 100KB per file
const ACCEPTED_EXTENSIONS = ['.txt', '.csv', '.md', '.json'];

const KnowledgeBaseSection: React.FC<{ config: any; onChange: (key: string, value: any) => void }> = ({ config, onChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const knowledgeFiles: Array<{ id: string; name: string; content: string; size: number }> = config.knowledgeFiles || [];

  const totalSize = knowledgeFiles.reduce((sum, f) => sum + f.size, 0) + (config.knowledgeBase?.length || 0);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;

    Array.from(files).forEach((file) => {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!ACCEPTED_EXTENSIONS.includes(ext)) {
        alert(`Formato não suportado: ${ext}\nAceitos: ${ACCEPTED_EXTENSIONS.join(', ')}`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        alert(`Arquivo muito grande: ${file.name}\nMáximo: 100KB por arquivo.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (ev) => {
        const content = ev.target?.result as string;
        const newFile = {
          id: Date.now().toString() + Math.random().toString(36).slice(2),
          name: file.name,
          content,
          size: file.size,
        };
        onChange('knowledgeFiles', [...knowledgeFiles, newFile]);
      };
      reader.readAsText(file);
    });

    // Reset input so the same file can be uploaded again if needed
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (id: string) => {
    onChange('knowledgeFiles', knowledgeFiles.filter((f) => f.id !== id));
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  return (
    <div className="border border-slate-200 dark:border-slate-600 rounded-lg p-3 space-y-3">
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
          Base de Conhecimento
        </label>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
          Adicione informações sobre produtos, serviços, FAQs, políticas, etc. A IA usará esse conteúdo como contexto para responder ao cliente.
        </p>
      </div>

      <div>
        <textarea
          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={4}
          placeholder={"Ex:\n- Nosso horário de atendimento é de segunda a sexta, das 9h às 18h.\n- O prazo de entrega é de 3 a 5 dias úteis.\n- Aceitamos Pix, cartão e boleto."}
          value={config.knowledgeBase || ''}
          onChange={(e) => onChange('knowledgeBase', e.target.value)}
        />
      </div>

      {/* Uploaded files */}
      {knowledgeFiles.length > 0 && (
        <div className="space-y-1.5">
          {knowledgeFiles.map((file) => (
            <div key={file.id} className="flex items-center justify-between bg-slate-50 dark:bg-slate-700/50 rounded-lg px-3 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                <span className="text-sm text-slate-700 dark:text-slate-200 truncate">{file.name}</span>
                <span className="text-xs text-slate-400 shrink-0">{formatSize(file.size)}</span>
              </div>
              <button
                onClick={() => removeFile(file.id)}
                className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded shrink-0"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload button */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium px-2 py-1 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded"
        >
          <Upload className="w-4 h-4" />
          Enviar arquivo
        </button>
        <span className="text-xs text-slate-400">
          {ACCEPTED_EXTENSIONS.join(', ')} — max 100KB cada
        </span>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS.join(',')}
          multiple
          onChange={handleFileUpload}
          className="hidden"
        />
      </div>

      {totalSize > 0 && (
        <p className="text-xs text-slate-400">
          Total de contexto: {formatSize(totalSize)}
        </p>
      )}
    </div>
  );
};
